"use client";

import React, { useRef, useState } from "react";
import Drawer from "antd/es/drawer";
import {
  requestGoogleConsentControls,
  useGoogleConsentControlsAvailable,
} from "@/lib/googleConsentUiBridge";
import { BookingPageHeader } from "./BookingPageHeader";
import type { BookingPageHeaderProps } from "./BookingPageHeader";
import BookingInfoPanel from "./BookingInfoPanel";
import type { BookingInfoPanelProps } from "./BookingInfoPanel";

export type BookingPageHeaderWithDrawerProps = Omit<
  BookingPageHeaderProps,
  "infoButtonRef"
> & {
  /** Props forwarded to the info drawer panel. */
  infoPanelProps?: BookingInfoPanelProps;
};

/**
 * Client wrapper around BookingPageHeader that adds
 * the mobile info drawer (same as the booking home page).
 */
export function BookingPageHeaderWithDrawer({
  infoPanelProps,
  ...headerProps
}: BookingPageHeaderWithDrawerProps) {
  const [drawerMounted, setDrawerMounted] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [openConsentAfterClose, setOpenConsentAfterClose] = useState(false);
  const infoButtonRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
  const consentControlsAvailable = useGoogleConsentControlsAvailable();

  const openPrivacyChoices = () => {
    setOpenConsentAfterClose(true);
    setInfoOpen(false);
  };

  return (
    <>
      <BookingPageHeader
        {...headerProps}
        infoButtonRef={infoButtonRef}
        onInfoClick={() => {
          setDrawerMounted(true);
          setInfoOpen(true);
        }}
      />
      {drawerMounted && (
        <Drawer
          title={headerProps.salonName}
          placement="right"
          onClose={() => setInfoOpen(false)}
          open={infoOpen}
          afterOpenChange={(open) => {
            if (open || !openConsentAfterClose) return;
            setOpenConsentAfterClose(false);
            requestGoogleConsentControls(infoButtonRef.current);
          }}
          width={380}
          // The drawer portals to document.body, outside the themed root's
          // inline vars; this class re-scopes the skin vars to the portal
          // (see EditorialBookingPageRoot). Without it, dark skins render
          // the info panel with light-fallback text and chips.
          rootClassName="openings-booking-portal openings-booking-info-drawer"
          styles={{
            header: { borderBottom: "none", fontWeight: 700 },
            body: { paddingTop: 0 },
          }}
        >
          <BookingInfoPanel {...infoPanelProps} />
          {consentControlsAvailable && (
            <button
              type="button"
              aria-haspopup="dialog"
              onClick={openPrivacyChoices}
              style={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: 40,
                marginTop: 12,
                padding: "8px 0",
                color: "var(--openings-color-text-secondary, #666)",
                background: "transparent",
                border: 0,
                font: "inherit",
                fontSize: 13,
                lineHeight: 1.4,
                textDecoration: "underline",
                textUnderlineOffset: 3,
                cursor: "pointer",
              }}
            >
              Privacy &amp; cookies
            </button>
          )}
        </Drawer>
      )}
    </>
  );
}
